import React from 'react';
import { css, keyframes } from '@emotion/react';
import {
  ReactComponent as AIPersona,
} from 'styles/icons/ai-persona.svg';
import ProfilePicture from 'org_level_profile/components/profile-picture';
import {
  genAIGradientType4,
  gray1,
  gray3,
  gray4,
  gray6,
  gray7,
  hexToRgbaString,
  lowTide,
} from 'styles/global_defaults/colors';
import { halfSpacing } from 'styles/global_defaults/scaffolding';
import { Role, VoiceCardState } from '../context';
import AudioVisualizationBubble from './audio-visualization-bubble';

type UserProfile = {
  profilePicture?: string;
  defaultPicture?: string;
  firstName?: string;
  lastName?: string;
};

type Props = {
  role: Role;
  name: string;
  profile?: UserProfile;
  state: VoiceCardState;
  mediaStream?: MediaStream;
};

const CARD_WIDTH = 380;
const CARD_HEIGHT = 240;
const IMAGE_SIZE = 100;
const PILL_HEIGHT = 30;
const PILL_BORDER_RADIUS = 15;
const PILL_OFFSET = halfSpacing;
const THINKING_PILL_WIDTH = 70;
const THINKING_PILL_HEIGHT = 35;

// scale(1.333) = 8px visual size from a 6px base, without touching width/height.
// 2.4s cycle; each dot's active window is the first third (0.8s).
// Negative delays pre-seed dots 2 and 3 so the sequence fires immediately on mount.
const dotPulse = keyframes`
  0%, 33.33%, 100% {
    transform: scale(1);
    background-color: ${gray4};
  }
  16.67% {
    transform: scale(1.333);
    background-color: ${gray3};
  }
`;

const thinkingDotsStyles = css`
  display: flex;
  gap: 4px;
  align-items: center;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${gray4};
    animation: ${dotPulse} 2.4s ease-in-out infinite;
  }
  .dot:nth-child(1) { animation-delay: 0s; }
  .dot:nth-child(2) { animation-delay: -1.6s; }
  .dot:nth-child(3) { animation-delay: -0.8s; }
`;

const VoiceCard = ({ role, name, profile, state, mediaStream }: Props) => {
  const isSpeaking = state === VoiceCardState.SPEAKING;
  const isThinking = state === VoiceCardState.THINKING;

  const styles = css`
    width: ${CARD_WIDTH}px;
    height: ${CARD_HEIGHT}px;
    border-radius: 15px;
    position: relative;
    border: 3px solid transparent;
    box-shadow: 1px 2px 2px 0px #0000001A;
    background-color: ${hexToRgbaString(gray7, 0.8)};

    ${isSpeaking && css`
      &::before {
        content: '';
        position: absolute;
        inset: -3px;
        border-radius: 15px;
        padding: 3px;
        background: ${genAIGradientType4};
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        mask-composite: exclude;
        opacity: 0.8;
      }
    `}

    .image-wrapper {
      width: ${IMAGE_SIZE}px;
      height: ${IMAGE_SIZE}px;
      border-radius: 50%;
    }

    .thinking-pill {
      top: ${PILL_OFFSET}px;
      right: ${PILL_OFFSET}px;
      width: ${THINKING_PILL_WIDTH}px;
      height: ${THINKING_PILL_HEIGHT}px;
      background-color: ${hexToRgbaString(lowTide, 0.15)};
      border-radius: ${THINKING_PILL_HEIGHT / 2}px;
    }

    .name-pill {
      bottom: ${PILL_OFFSET}px;
      left: ${PILL_OFFSET}px;
      height: ${PILL_HEIGHT}px;
      max-width: calc(100% - ${PILL_OFFSET * 2}px);
      border-radius: ${PILL_BORDER_RADIUS}px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      background-color: ${hexToRgbaString(gray6, 0.7)};
    }
  `;

  return (
    <div css={styles} className='position-relative d-flex align-items-center justify-content-center'>
      <div className='image-wrapper display-flex align-items-center justify-content-center'>
        {role === Role.AI ? (
          <AIPersona
            width={IMAGE_SIZE}
            height={IMAGE_SIZE}
            aria-hidden
          />
        ) : (
          <ProfilePicture
            isRound
            size={IMAGE_SIZE}
            profile={profile ?? {}}
          />
        )}
      </div>
      <div className='name-pill text-small d-flex position-absolute text-gray-1 align-items-center px-4 font-weight-bolder'>
        {name}
      </div>
      {isSpeaking && (
        <AudioVisualizationBubble mediaStream={mediaStream} />
      )}
      {isThinking && (
        <div className='thinking-pill position-absolute d-flex align-items-center justify-content-center'>
          <div css={thinkingDotsStyles}>
            <div className='dot' />
            <div className='dot' />
            <div className='dot' />
          </div>
        </div>
      )}
    </div>
  );
};

export default VoiceCard;
